import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import debounce from "lodash/debounce";
import { USER_STATUSES } from "constants/Constants";

import useAuthStore from "stores/useAuthStore";
import ACL_RELATIONSHIPS from "acl-relationships";
import userService from "services/User";
import { retryOnError } from "services/helpers";
import { useSocket } from "providers/SocketProvider";
import useAuthorized from "hooks/useAuthorized";
import useAuthenticated from "hooks/useAuthenticated";

const UserStatusContext = createContext();

const SYNC_DEBOUNCE_MS = 500;
const SYNC_RETRY_TIMEOUT_MS = 10000;
const SYNC_RETRY_DELAY_MS = 1000;

export const useUserStatus = () => useContext(UserStatusContext);

export const UserStatusProvider = ({ children }) => {
  const user = useAuthStore.getState().userAuth;
  const [status, setStatus] = useState(USER_STATUSES.DISCONNECTED.key);
  const [isDisabled, setIsDisabled] = useState(false);
  const { socket } = useSocket();
  const canEditStatus = useAuthorized([ACL_RELATIONSHIPS.callAgentStatus.edit]);
  const isAuthenticated = useAuthenticated();

  const syncStatus = useCallback(async () => {
    if (!user) return;
    try {
      const savedStatus = await retryOnError(
        () => userService.getUserStatus(user.id),
        SYNC_RETRY_TIMEOUT_MS,
        SYNC_RETRY_DELAY_MS
      );
      setStatus(savedStatus);
      setIsDisabled(
        !canEditStatus ||
          savedStatus === USER_STATUSES.DISCONNECTED.key ||
          savedStatus === USER_STATUSES.DISABLED.key ||
          savedStatus === USER_STATUSES.ON_CALL.key
      );
    } catch {
      setStatus(USER_STATUSES.OFFLINE.key);
      setIsDisabled(true);
    }
  }, [user, canEditStatus]);

  const updateStatus = useCallback(
    async status => {
      await userService.updateUserStatus(user.id, { status });
      setStatus(status);
    },
    [user]
  );

  const syncStatusRef = useRef(syncStatus);
  useEffect(() => {
    syncStatusRef.current = syncStatus;
  }, [syncStatus]);

  useEffect(() => {
    if (!socket || !isAuthenticated) return;

    const debouncedSyncStatus = debounce(
      () => syncStatusRef.current(),
      SYNC_DEBOUNCE_MS,
      { leading: true, trailing: true }
    );

    debouncedSyncStatus();
    const handleDisconnect = () => {
      setStatus(USER_STATUSES.DISCONNECTED.key);
      setIsDisabled(true);
    };

    socket.on("disconnect", handleDisconnect);
    socket.on("connect", debouncedSyncStatus);
    socket.on("status_update", debouncedSyncStatus);
    return () => {
      debouncedSyncStatus.cancel();
      socket.off("disconnect", handleDisconnect);
      socket.off("connect", debouncedSyncStatus);
      socket.off("status_update", debouncedSyncStatus);
    };
  }, [socket, isAuthenticated]);

  return (
    <UserStatusContext.Provider
      value={{
        isDisabled,
        status,
        updateStatus,
      }}
    >
      {children}
    </UserStatusContext.Provider>
  );
};
